<script setup>
import { useI18n } from 'vue-i18n';
import { ref, watch, nextTick } from 'vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';

const props = defineProps({
  messages: {
    type: Array,
    required: true,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  thinkingLabel: {
    type: String,
    default: '',
  },
});

const messageContainer = ref(null);

const { t } = useI18n();
const { formatMessage } = useMessageFormatter();

const isUserMessage = sender => sender === 'user';

const getAvatarName = sender =>
  isUserMessage(sender)
    ? t('CAPTAIN.PLAYGROUND.USER')
    : t('CAPTAIN.PLAYGROUND.ASSISTANT');

// Resolve a raw tool name (e.g. 'search_documentation') to a human-friendly label.
const resolveToolLabel = toolName => {
  const key = `CAPTAIN.PLAYGROUND.THINKING_TOOLS.${toolName}`;
  const resolved = t(key);
  return resolved === key ? toolName : resolved;
};

const getToolBadges = message => {
  if (!message.toolCallsMade || !message.toolCallsMade.length) return [];
  return message.toolCallsMade.map(resolveToolLabel);
};

const scrollToBottom = async () => {
  await nextTick();
  if (messageContainer.value) {
    messageContainer.value.scrollTop = messageContainer.value.scrollHeight;
  }
};

watch(() => props.messages.length, scrollToBottom);
watch(() => props.isLoading, scrollToBottom);
</script>

<template>
  <div
    ref="messageContainer"
    class="flex-1 overflow-y-auto mb-4 px-6 space-y-6"
  >
    <div
      v-for="(message, index) in messages"
      :key="index"
      class="flex flex-col"
    >
      <!-- Assistant Message: Aligned LEFT -->
      <template v-if="!isUserMessage(message.sender)">
        <div class="flex items-end gap-2 max-w-[85%] md:max-w-[70%] justify-start">
          <Avatar
            :name="getAvatarName(message.sender)"
            rounded-full
            :size="26"
            class="shrink-0 mb-0.5"
          />
          <div
            class="px-4 py-3 text-sm [overflow-wrap:break-word] bg-n-solid-iris text-n-slate-12 rounded-bl-sm rounded-br-xl rounded-t-xl shadow-xs"
          >
            <div v-html="formatMessage(message.content)" />
          </div>
        </div>

        <!-- Sources checked badges below assistant message -->
        <div
          v-if="getToolBadges(message).length"
          class="mt-2 ml-8 flex flex-wrap gap-1.5 items-center"
        >
          <span class="text-xs text-n-slate-10 font-medium mr-0.5 select-none">
            {{ t('CAPTAIN.PLAYGROUND.SOURCES_CHECKED') }}:
          </span>
          <span
            v-for="badge in getToolBadges(message)"
            :key="badge"
            class="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs bg-n-alpha-1 text-n-slate-11 border border-n-weak shadow-2xs"
          >
            <span class="i-lucide-search-check w-3 h-3 shrink-0 opacity-70" />
            {{ badge }}
          </span>
        </div>
      </template>

      <!-- User Message: Aligned RIGHT -->
      <template v-else>
        <div class="flex items-end gap-2 max-w-[85%] md:max-w-[70%] justify-end ml-auto">
          <div
            class="px-4 py-3 text-sm [overflow-wrap:break-word] bg-n-solid-blue text-n-slate-12 rounded-br-sm rounded-bl-xl rounded-t-xl shadow-xs"
          >
            <div v-html="formatMessage(message.content)" />
          </div>
          <Avatar
            :name="getAvatarName(message.sender)"
            rounded-full
            :size="24"
            class="shrink-0 mb-0.5"
          />
        </div>
      </template>
    </div>

    <!-- Loading / thinking indicator -->
    <div v-if="isLoading" class="flex justify-start">
      <div class="flex items-start gap-2">
        <Avatar :name="getAvatarName('assistant')" rounded-full :size="26" class="shrink-0" />
        <div
          class="max-w-sm rounded-lg p-3 text-sm bg-n-solid-iris text-n-slate-12"
        >
          <div v-if="thinkingLabel" class="flex items-center gap-2">
            <span class="i-lucide-loader-2 w-3.5 h-3.5 animate-spin shrink-0 opacity-80" />
            <span class="text-xs text-n-slate-11 animate-pulse">{{ thinkingLabel }}</span>
          </div>
          <div v-else class="flex gap-1">
            <div class="w-2 h-2 rounded-full bg-n-iris-10 animate-bounce" />
            <div
              class="w-2 h-2 rounded-full bg-n-iris-10 animate-bounce [animation-delay:0.2s]"
            />
            <div
              class="w-2 h-2 rounded-full bg-n-iris-10 animate-bounce [animation-delay:0.4s]"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
